import { motion } from "framer-motion";
import { ShieldCheck, ScrollText } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.7, ease: [0.19, 1, 0.22, 1] },
  }),
};

const SECTIONS = [
  {
    id: "terms",
    icon: ScrollText,
    eyebrow: "Part I",
    title: "Terms & Conditions",
    intro:
      "These terms govern every quote, order and installation carried out by Eurosteel NZ. By placing an order you accept the terms below and the process set out in our Customer Care Policy.",
    blocks: [
      {
        heading: "1 · Quotations & Pricing",
        body: "All quotations are valid for thirty (30) days from the date of issue. Prices are quoted in NZD and exclude GST unless otherwise stated. Steel, aluminium and freight surcharges beyond our control may be passed through if raw-material pricing shifts materially between quote and order confirmation.",
      },
      {
        heading: "2 · Deposit & Payment Terms",
        body: "A seventy percent (70%) deposit is required to commence production of your custom Eurosteel doors or windows. The remaining balance must be paid in full prior to shipment from our manufacturing facility. Once production is complete our accounts team will send final images along with the outstanding invoice — you have seven (7) days to settle the balance. If payment is not received within this timeframe, storage fees will apply on a daily basis, depending on order size and quantity. Goods will not be released for installation, delivery or collection until full payment has been received in cleared funds.",
      },
      {
        heading: "3 · Lead Times",
        body: "Standard production lead time is fourteen (14) to twenty (20) weeks, with current lead times typically sitting at approximately sixteen (16) weeks. The official lead time begins on receipt of your deposit and signed shop drawings, and will be confirmed to you in writing by email. Delays caused by supplier, freight, powder-coater or force majeure are outside our reasonable control.",
      },
      {
        heading: "4 · Site Measurements — Client Responsibility",
        body: "All orders are manufactured strictly to the dimensions supplied by you or your builder, down to the millimetre. It is your responsibility to confirm these measurements with a qualified New Zealand professional prior to ordering. Eurosteel NZ is not liable for any incorrect measurements provided at time of order, or for any discrepancies once shop drawings have been signed off.",
      },
      {
        heading: "5 · Cancellations & Refunds",
        body: "A seven (7) day cooling-off period applies from the date of deposit payment. If you cancel after this cooling-off period but before production begins, an administration fee of $1,500 + GST or five percent (5%) of the total order value — whichever is greater — will be deducted from your deposit refund. Once shop drawings are signed off and manufacturing has commenced, no refunds are permitted and the full balance is payable on completion. As all products are custom made to order, no refunds are issued for errors arising from incorrect information supplied to us.",
      },
      {
        heading: "6 · Delivery & Site Access",
        body: "Deliveries are made throughout New Zealand. Delivery is made to the front of the site only, in a safe, flat location — drivers do not place crates into garages, laneways or other internal site areas. You must be present at the delivery or provide written authority to leave the crates. Missed delivery windows or redelivery attempts will incur additional freight charges. For rural or outlying addresses, please contact us directly for a freight estimate. The site must be ready to receive the goods — Eurosteel does not offer storage services.",
      },
      {
        heading: "7 · Inspection on Arrival",
        body: "Before unpacking or installing, inspect every item while still in its original packaging. If any damage or defects are found, photograph them immediately. Claims made after installation, or without supporting images taken at the time of unpacking, will not be accepted. Crates must be kept dry and covered on-site to preserve product condition and maintain warranty eligibility.",
      },
      {
        heading: "8 · Installation",
        body: "Eurosteel offers professional installation through our network of experienced installers — we will coordinate the schedule, site access and post-install support. If you choose to use your own installer or builder, Eurosteel takes no responsibility for incorrect installation, site measurements or damages resulting from third-party work. It is essential your installer reviews the final signed-off shop drawings before beginning installation.",
      },
      {
        heading: "9 · Handcrafted Tolerances",
        body: "Every Eurosteel product is handcrafted from steel. Minor cosmetic imperfections in steelwork, welds or paint finish are to be expected and fall within acceptable tolerance levels for handcrafted steelwork. If a minor fault is identified that sits within our defined tolerance, we will offer a free repair or another appropriate remedy.",
      },
      {
        heading: "10 · Title, Risk & Liability",
        body: "Title in all goods remains with Eurosteel NZ until payment is received in full. Risk passes to the client on dispatch from our factory. To the maximum extent permitted by law, Eurosteel's liability is limited to the repair or replacement of the goods supplied. We are not liable for consequential loss, loss of profit, or programme delay damages arising from installation.",
      },
      {
        heading: "11 · Consumer Guarantees & Governing Law",
        body: "These terms are governed by the laws of New Zealand. Nothing in these terms limits your rights under the Consumer Guarantees Act 1993. However, refunds and replacements do not apply where you have changed your mind, no longer need the product, misused the product, were aware of any faults before purchase, or asked for a product to be made or installed contrary to our advice.",
      },
    ],
  },
  {
    id: "warranty",
    icon: ShieldCheck,
    eyebrow: "Part II",
    title: "Limited Warranty",
    intro:
      "Every Eurosteel window and door is covered by a five-year Limited Warranty from the date of delivery. This warranty runs alongside your rights under the New Zealand Consumer Guarantees Act 1993.",
    blocks: [
      {
        heading: "1 · Warranty Period",
        body: "The Warranty Period for all physical goods purchased from Eurosteel NZ is five (5) years from the date of delivery for any manufacturing defects in materials or workmanship, provided the product is under normal use and proper maintenance.",
      },
      {
        heading: "2 · What Is Covered",
        body: "Manufacturing defects in materials or workmanship that are not fair wear and tear. If a product or part of it is found to be defective due to a manufacturing fault, we will repair the item, or replace it if it cannot be repaired to our standards.",
      },
      {
        heading: "3 · What Is NOT Covered",
        body: "Incorrect installation not in accordance with Eurosteel's guidelines; general wear and tear or damage caused by misuse or poor maintenance; damage caused by trades, mishandling or on-site accidents; paint-finish issues or rusting from excessive water exposure or prolonged direct sunlight where the product is installed without adequate overhead protection (at least 1–1.5 metres of eave cover); water ingress where products are installed without appropriate sheltering; and any tampering or modification without written approval from Eurosteel NZ.",
      },
      {
        heading: "4 · Finish Specification",
        body: "All Eurosteel products are hot-dip galvanised, primed with a zinc-based coating and painted with a high-performance fluorocarbon finish for superior corrosion resistance. For coastal or salt-air sites we specify the marine-grade finish system and recommend the W75TB Inox stainless-steel profile — quarterly fresh-water rinse-down is required to maintain the finish warranty.",
      },
      {
        heading: "5 · Care Instructions",
        body: "Wipe frames every three months with warm water and a soft cloth. Lubricate hardware annually with a light dry PTFE spray. Do not use ammonia, bleach, solvents or high-pressure cleaners — these will damage the fluorocarbon finish and void the warranty.",
      },
      {
        heading: "6 · Major Faults",
        body: "A product will be deemed to have a major fault if it has a problem that would have stopped a reasonable person from buying it; it is significantly different from its description or sample; it is substantially unfit for purpose and cannot be easily fixed; or it is unsafe or does not perform as expected. Where a major fault is confirmed you may be entitled to a repair, replacement, refund or compensation under the Consumer Guarantees Act 1993.",
      },
      {
        heading: "7 · Claims Procedure",
        body: "To make a claim under this warranty, contact us first so we can assess the issue and discuss next steps — phone 027 915 2526 or email isaiah@euro-steel.co.nz. Claims must be accompanied by your invoice number and supporting photographs. Eurosteel will inspect within a reasonable period and, at its option, repair, replace or refinish the affected component.",
      },
      {
        heading: "8 · Statutory Rights",
        body: "This warranty is in addition to, and does not limit, your rights under the New Zealand Consumer Guarantees Act 1993 where applicable. Where the product is acquired for the purposes of a business, the guarantees under the Act do not apply.",
      },
    ],
  },
];

export default function Terms() {
  return (
    <div
      data-testid="terms-page"
      className="relative min-h-screen bg-[#0a0a0c] pt-32 lg:pt-40"
    >
      {/* Hero */}
      <section className="mx-auto max-w-[1200px] px-6 pb-16 lg:px-12">
        <motion.h1
          initial="hidden"
          animate="show"
          variants={fade}
          custom={1}
          className="font-serif-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          data-testid="terms-heading"
        >
          Terms & Warranty
        </motion.h1>
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={2}
          className="mt-8 max-w-2xl font-body text-base leading-relaxed text-white/60 sm:text-lg"
        >
          The commercial framework and product guarantees that stand behind every Eurosteel window,
          door, and hardware piece we deliver.
        </motion.p>
        <motion.div
          initial="hidden"
          animate="show"
          variants={fade}
          custom={3}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href="#terms"
            data-testid="terms-jump-terms"
            className="rounded-full border border-white/15 px-6 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
          >
            Read Terms
          </a>
          <a
            href="#warranty"
            data-testid="terms-jump-warranty"
            className="rounded-full border border-white/15 px-6 py-3 font-tech text-[10px] uppercase tracking-[0.24em] text-white/80 transition-colors hover:border-[#c6a87c] hover:text-[#c6a87c]"
          >
            Read Warranty
          </a>
        </motion.div>
        <motion.p
          initial="hidden"
          animate="show"
          variants={fade}
          custom={4}
          className="mt-10 font-tech text-[10px] uppercase tracking-[0.3em] text-white/30"
        >
          Version 1.0 · Effective {new Date().toLocaleString("en-NZ", { month: "long", year: "numeric" })}
        </motion.p>
      </section>

      <div className="mx-auto max-w-[1200px] border-t border-white/10" />

      {/* Sections */}
      {SECTIONS.map((section) => {
        const Icon = section.icon;
        return (
          <section
            key={section.id}
            id={section.id}
            data-testid={`terms-section-${section.id}`}
            className="mx-auto max-w-[1200px] scroll-mt-32 px-6 py-24 lg:px-12"
          >
            <div className="grid gap-16 lg:grid-cols-[280px_1fr]">
              <div className="lg:sticky lg:top-32 lg:self-start">
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#c6a87c]/40 text-[#c6a87c]">
                  <Icon size={18} strokeWidth={1.4} />
                </div>
                <p className="mt-6 font-tech text-[10px] uppercase tracking-[0.3em] text-[#c6a87c]/80">
                  {section.eyebrow}
                </p>
                <h2 className="mt-3 font-serif-display text-3xl leading-tight text-white lg:text-4xl">
                  {section.title}
                </h2>
                <p className="mt-5 font-body text-sm leading-relaxed text-white/55">
                  {section.intro}
                </p>
              </div>

              <div className="space-y-10">
                {section.blocks.map((block, i) => (
                  <motion.div
                    key={block.heading}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ delay: i * 0.04, duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
                    className="border-l border-white/10 pl-6"
                  >
                    <h3 className="font-tech text-[11px] uppercase tracking-[0.24em] text-[#c6a87c]">
                      {block.heading}
                    </h3>
                    <p className="mt-3 font-body text-[15px] leading-[1.75] text-white/75">
                      {block.body}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Footer CTA */}
      <section className="mx-auto max-w-[1200px] border-t border-white/10 px-6 py-20 lg:px-12">
        <div className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
              Need clarification?
            </p>
            <h3 className="mt-4 max-w-xl font-serif-display text-2xl leading-tight text-white lg:text-3xl">
              Talk to us before you place your order — we'd rather over-explain than under-deliver.
            </h3>
          </div>
          <a
            href="mailto:isaiah@euro-steel.co.nz?subject=Terms%20%26%20Warranty%20Query"
            data-testid="terms-contact-btn"
            className="whitespace-nowrap rounded-full bg-[#c6a87c] px-8 py-3 font-tech text-[11px] uppercase tracking-[0.2em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
          >
            Get in Touch
          </a>
        </div>
      </section>
    </div>
  );
}
